import React from 'react';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import LayersIcon from '@mui/icons-material/Layers';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { formatEventDateTime } from '../utils/eventTypes';
import PersonAvatar from '../shared/PersonAvatar';
import { UI_TIMINGS } from '../../../config/appConfig';
import { getInterestLevelColor, shouldInterestLevelPulse } from '../utils/interestLevel';
import { useSearch } from '../../../contexts/searchContext';

/**
 * MultiDatasetMatchEventListItem - Displays a multi-dataset match event
 * Shows when the same person is matched across multiple datasets
 * @param {Object} event - Event data
 * @param {Function} onClick - Callback when item is clicked
 * @param {Function} onDelete - Callback when delete button is clicked
 * @param {boolean} disabled - Whether the item is disabled (e.g., during loading)
 * @param {boolean} showDelete - Whether to show the delete button
 */
function MultiDatasetMatchEventListItem({ event, onClick, onDelete, disabled = false, showDelete = true }) {
  const { datasetObjects } = useSearch();

  const dateTimeStr = formatEventDateTime(event.createdAt);
  const personName = event.metadata?.personName || 'Unknown';

  // Shorten to first two words and limit to 20 chars
  const words = personName.split(' ');
  const firstTwoWords = words.length > 2 ? words.slice(0, 2).join(' ') : personName;
  const displayName = firstTwoWords.length > 20 ? `${firstTwoWords.substring(0, 20)}...` : firstTwoWords;

  // Backend provides full URLs - no conversion needed
  const personImages = event.metadata?.personImages || [];
  const datasets = event.metadata?.datasets || [];
  const datasetCount = event.metadata?.datasetCount || datasets.length;
  const capturedImageUrl = event.metadata?.capturedImageUrl;

  // Get primary dataset icon from first dataset
  const primaryDatasetId = datasets[0];
  const primaryDataset = datasetObjects.find(ds => ds.dataset_id === primaryDatasetId);
  const datasetIconUrl = primaryDataset?.icon_url;

  // Interest level indicator (from first person record)
  const persons = event.metadata?.persons || [];
  const primaryPerson = persons[0];
  // Unified schema: read from interest object
  const interestLevel = primaryPerson?.interest?.level;
  const interestReason = primaryPerson?.interest?.reason;
  const interestColor = getInterestLevelColor(interestLevel);
  const shouldPulse = shouldInterestLevelPulse(interestLevel);

  const [swipeX, setSwipeX] = React.useState(0);
  const [isSwiping, setIsSwiping] = React.useState(false);
  const [isMouseDown, setIsMouseDown] = React.useState(false);
  const startX = React.useRef(0);
  const currentX = React.useRef(0);

  const handleDeleteClick = React.useCallback((e) => {
    e.stopPropagation();
    onDelete(event);
  }, [onDelete, event]);

  const handleClick = React.useCallback(() => {
    if (!isSwiping) {
      onClick(event);
    }
  }, [onClick, event, isSwiping]);

  const handleStart = (clientX) => {
    startX.current = clientX;
    currentX.current = clientX;
    setIsSwiping(false);
  };

  const handleMove = (clientX) => {
    currentX.current = clientX;
    const diff = currentX.current - startX.current;

    // Only allow left swipe (negative values)
    if (diff < 0) {
      setSwipeX(Math.max(diff, -100)); // Limit to -100px
      if (Math.abs(diff) > 10) {
        setIsSwiping(true);
      }
    }
  };

  const handleEnd = () => {
    const diff = currentX.current - startX.current;

    // If swiped more than 60px, trigger delete
    if (diff < -60) {
      onDelete(event);
    } else {
      // Reset position
      setSwipeX(0);
      setTimeout(() => setIsSwiping(false), UI_TIMINGS.SWIPE_ANIMATION_CLEANUP);
    }
    setIsMouseDown(false);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (isMouseDown) {
      handleMove(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (isMouseDown) {
      handleEnd();
    }
  };

  const handleMouseLeave = () => {
    if (isMouseDown) {
      handleEnd();
    }
  };

  // Color scheme - purple tint for multi-dataset matches
  const isInAssignment = !!event.metadata?.assignmentEventId;
  const backgroundColor = isInAssignment
    ? '#f5f5f5'  // Light grey for events in assignments
    : '#f3e5f5';  // Light purple for multi-dataset
  const borderColor = isInAssignment
    ? '#ffcc80'  // Light orange for events in assignments
    : 'rgba(142, 36, 170, 0.4)';  // Purple for multi-dataset

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 1,
        overflow: 'hidden',
        borderRadius: 1
      }}
    >
      {/* Dataset icon - top left corner */}
      {datasetIconUrl && (
        <Tooltip title={primaryDataset?.dataset_name || primaryDatasetId} arrow placement="right">
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              left: 10,
              width: 24,
              height: 24,
              zIndex: 2,
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }}
          >
            <img
              src={datasetIconUrl}
              alt={primaryDatasetId}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
        </Tooltip>
      )}

      {/* Delete background - revealed during swipe */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 100,
          backgroundColor: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          zIndex: 0
        }}
      >
        <DeleteIcon />
      </Box>

      {/* Main list item - slides left on swipe */}
      <ListItem
        disablePadding
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          height: 80,
          backgroundColor: `${backgroundColor} !important`,
          borderLeft: `6px solid ${borderColor}`,
          borderRadius: 1,
          pl: 2,
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
          cursor: isMouseDown ? 'grabbing' : 'grab',
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: backgroundColor,
            zIndex: -1
          }
        }}
        secondaryAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Multi-dataset indicator and assignment indicator in same column */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              {event.metadata?.assignmentEventId && (
                <Tooltip title={event.metadata.assignmentEventId} arrow>
                  <AssignmentIcon sx={{ fontSize: 16, color: '#1976d2', opacity: 0.7 }} />
                </Tooltip>
              )}
              {/* Multi-dataset indicator with dataset count */}
              <Tooltip title={`Matched in ${datasetCount} datasets: ${datasets.join(', ')}`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <LayersIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                  <Box
                    sx={{
                      fontSize: 10,
                      fontWeight: 'bold',
                      color: 'secondary.main',
                      lineHeight: 1
                    }}
                  >
                    {datasetCount}
                  </Box>
                </Box>
              </Tooltip>
            </Box>
            {false && showDelete && (
              <IconButton
                edge="end"
                aria-label="delete"
                size="small"
                onClick={handleDeleteClick}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
      >
        <ListItemButton
          onClick={handleClick}
          disabled={disabled}
          disableRipple={isSwiping}
          sx={{
            height: '100%',
            backgroundColor: 'inherit',
            '&:hover': {
              backgroundColor: 'inherit'
            },
            '& .MuiTouchRipple-root': {
              display: 'none'
            }
          }}
        >
          {/* Person avatar with interest level indicator */}
          <Tooltip title={interestReason || (interestLevel ? `Interest: ${interestLevel}` : '')} arrow placement="right" disableHoverListener={!interestLevel}>
            <Box
              onClick={(e) => {
                const personUri = primaryPerson?.uri;
                if (personUri) {
                  e.stopPropagation();
                  window.open(personUri, '_blank', 'noreferrer');
                }
              }}
              sx={{
                cursor: primaryPerson?.uri ? 'pointer' : 'default',
                '&:hover': primaryPerson?.uri ? {
                  opacity: 0.8
                } : {},
                border: interestLevel ? `3px solid ${interestColor}` : 'none',
                borderRadius: '50%',
                padding: interestLevel ? '2px' : 0,
                display: 'inline-flex',
                mr: 2,
                '& .MuiAvatar-root': { mr: 0 },
                animation: shouldPulse ? 'pulse 1.5s ease-in-out infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': {
                    boxShadow: `0 0 0 0 ${interestColor}99`,
                    transform: 'scale(1)'
                  },
                  '50%': {
                    boxShadow: `0 0 0 12px ${interestColor}00`,
                    transform: 'scale(1.05)'
                  }
                }
              }}
            >
              {/* Show all images in circular layout */}
              {personImages.length === 1 ? (
                <PersonAvatar imageUrl={personImages[0]?.imageUrl} name={personName} size={64} />
              ) : personImages.length === 2 ? (
                // Two images: split circle vertically
                <Box sx={{ position: 'relative', width: 64, height: 64 }}>
                  <Box
                    component="img"
                    src={personImages[0].imageUrl}
                    alt={personImages[0].dataset}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: 32,
                      height: 64,
                      objectFit: 'cover',
                      borderRadius: '32px 0 0 32px'
                    }}
                  />
                  <Box
                    component="img"
                    src={personImages[1].imageUrl}
                    alt={personImages[1].dataset}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      width: 32,
                      height: 64,
                      objectFit: 'cover',
                      borderRadius: '0 32px 32px 0'
                    }}
                  />
                </Box>
              ) : (
                // Three or more images: quadrant layout (top-left, top-right, bottom-left, bottom-right)
                <Box sx={{ position: 'relative', width: 64, height: 64, borderRadius: '50%', overflow: 'hidden' }}>
                  {personImages.slice(0, 4).map((img, idx) => {
                    const positions = [
                      { top: 0, left: 0, borderRadius: '100% 0 0 0' }, // top-left
                      { top: 0, right: 0, borderRadius: '0 100% 0 0' }, // top-right
                      { bottom: 0, left: 0, borderRadius: '0 0 0 100%' }, // bottom-left
                      { bottom: 0, right: 0, borderRadius: '0 0 100% 0' } // bottom-right
                    ];
                    return (
                      <Box
                        key={idx}
                        component="img"
                        src={img.imageUrl}
                        alt={img.dataset}
                        sx={{
                          position: 'absolute',
                          width: 32,
                          height: 32,
                          objectFit: 'cover',
                          ...positions[idx]
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          </Tooltip>
          <ListItemText
            primary={displayName}
            secondary={dateTimeStr}
          />
        </ListItemButton>
      </ListItem>
    </Box>
  );
}

export default React.memo(MultiDatasetMatchEventListItem);
